import React from 'react';
import PropTypes from 'prop-types';
import {Switch, Route, useLocation, useHistory} from 'react-router-dom';
import {Translate} from 'react-redux-i18n';

import {InitialLoading} from 'front-onceforall-core';
import {useStateHook} from 'front-onceforall-core/dist/utils/customHooks';

import authenticationService from '../services/authentication/AuthenticationService';
import {captureError, consoleLog} from '../utils/log';
import AppLayout from '../layout/AppLayout';
import UserActivationView from '../components/userActivation';
import TextsValidation from '../components/textsValidation';

import {TIME_BEFORE_FAILURE, getTextsToValidate} from './utils';

function AuthenticationGate(props) {
  const [state, setState] = useStateHook({
    error: false,
    loading: true,
    textsToValidate: [],
    loadingMessage: <Translate value="initialLoading.message"/>
  });
  const timeoutBeforeFailureRef = React.useRef(null);
  const location = useLocation();
  const history = useHistory();

  const checkAuthentication = () => {
    setState({loading: true});
    if (props.store.getState().user && location.pathname === '/activate-account') {
      setState({loading: false});
      return;
    }

    authenticationService.init(props.store, location)
      .then(() => {
        if (!props.store.getState().user.emailVerified) {
          history.replace({pathname: '/activate-account'});
        }

        return getTextsToValidate(props.store.getState().user);
      })
      .then(textsToValidate => {
        setState({textsToValidate, loading: false, error: false});
      })
      .catch(error => {
        authenticationService.logout();
        captureError('Authentication error', error);
        setState({
          error: true,
          loading: false
        });
      })
      .finally(() => {
        clearTimeout(timeoutBeforeFailureRef.current);
      });
  };

  React.useEffect(() => {
    checkAuthentication();
    if (location.pathname !== '/activate-account') {
      consoleLog('Initial loading timer start');

      timeoutBeforeFailureRef.current = setTimeout(() => {
        setState({loadingMessage: <Translate value="initialLoading.error"/>});
        const error = new Error(`Initial loading timeout for user ${props.store.getState().user?.id} on ${location.pathname}`);
        captureError(`Initial loading timeout (after ${TIME_BEFORE_FAILURE / 1000} sec)`, error);
        authenticationService.login(props.store, location);
      }, TIME_BEFORE_FAILURE);
    }

    return () => {
      clearTimeout(timeoutBeforeFailureRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (!state.loading) {
      clearTimeout(timeoutBeforeFailureRef.current);
    }
  }, [state.loading]);

  const renderApp = () => (
    state.textsToValidate.length > 0 ?
      <TextsValidation texts={state.textsToValidate}/> :
      <Switch>
        <Route
          exact
          path="/activate-account"
          component={UserActivationView}
        />
        <Route component={AppLayout}/>
      </Switch>
  );

  if (state.error) {
    return <div><Translate value="error.generic"/></div>;
  }

  return (
    <>
      <InitialLoading
        show={state.loading}
        text={state.loadingMessage}
      />
      {!state.loading && renderApp()}
    </>
  );
}

AuthenticationGate.propTypes = {
  store: PropTypes.object.isRequired
};

export default AuthenticationGate;
